// export default MgtForm;

import React, { useState, useEffect } from "react";
import FormField from "../../../components/ui/FormField";
import { useSelector } from "react-redux";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { mgt1Api } from "../../../api/MgtApi1";
import { Button } from "../../../components/Button";

// Helper functions to create initial state objects
const createPersonalDetails = () => ({
  memberName: "",
  jointHoldersName: "",
  addressOrRegisteredAddress: "",
  emailId: "",
  cinOrRegistrationNo: "",
  uniqueIdentificationNo: "",
  parentOrSpouseName: "",
  status: "",
  occupation: "",
  panNo: "",
  nationality: "Indian",
});

const createMemberMinorDetails = () => ({
  guardianName: "",
  dateOfBirthOfMinor: "",
  dateOfBecomingMember: "",
  memberShipDetails: {
    declarationDateUnderSection89: "",
    beneficialOwnerNameAndAddress: "",
    nominationReceiptDate: "",
    nomineeNameAndAddress: "",
    sharesKeptInAbeyance: "",
    lienOnShares: "",
    dateOfCessationOfMembership: "",
  },
});

const createShareHolding = () => ({
  allotmentOrTransferNo: "",
  dateOfAllotmentOrTransfer: "",
  numberOfSharesAllottedOrTransferred: "",
  distinctiveNumberRanges: [],
  folioOfTransferor: "",
  nameOfTransferor: "",
  dateOfIssueOrEndorsement: "",
  certificateNo: "",
  lockInPeriod: "",
  amount: {
    payable_Amount: "",
    paidOrDeemed_Amount: "",
    due_Amount: "",
  },
  considerationOtherThanCash: "",
  dateOfTransferOrTransmission: "",
  numberOfSharesTransferredOrTransmitted: "",
  transferDistinctiveNumberRanges: [],
  folioOfTransferee: "",
  nameOfTransferee: "",
  balanceSharesAfterTransaction: "",
  remarks: "",
  authenticationOrSignature: "",
});

const MgtForm = () => {
  const token = useSelector((state) => state.auth.access_token);

  const [formData, setFormData] = useState({
    accountId: "",
    companyName: "",
    shareClass: "",
    subClass: "",
    nominalValuePerShare: "",
    totalSharesHeld: "",
    personalDetails: createPersonalDetails(),
    memberMinorDetails: createMemberMinorDetails(),
    mgt1ShareHolding: [createShareHolding()],
  });

  const companies = useSelector((state) => state.account.accountDetails);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleCompanyChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") {
      setFormData((prev) => ({ ...prev, accountId: "", companyName: "" }));
      return;
    }
    const selectedCompany = companies.find(
      (company) => String(company.id) === String(selectedValue),
    );
    if (selectedCompany) {
      setFormData((prev) => ({
        ...prev,
        accountId: selectedCompany.id,
        companyName: selectedCompany.accountName || "",
      }));
    } else {
      console.error("Company not found for value:", selectedValue);
    }
  };

  const handleRootChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePersonalChange = (section, field, value) => {
    setFormData((prev) => {
      if (section === "personalDetails") {
        return {
          ...prev,
          personalDetails: { ...prev.personalDetails, [field]: value },
        };
      }
      if (section === "memberMinorDetails") {
        return {
          ...prev,
          memberMinorDetails: { ...prev.memberMinorDetails, [field]: value },
        };
      }
      if (section === "memberShipDetails") {
        return {
          ...prev,
          memberMinorDetails: {
            ...prev.memberMinorDetails,
            memberShipDetails: {
              ...prev.memberMinorDetails.memberShipDetails,
              [field]: value,
            },
          },
        };
      }
      return prev;
    });
  };

  const handleShareHoldingChange = (
    index,
    field,
    value,
    subField = null,
    rangeIndex = null,
  ) => {
    setFormData((prev) => {
      const updated = [...prev.mgt1ShareHolding];
      if (
        (field === "distinctiveNumberRanges" ||
          field === "transferDistinctiveNumberRanges") &&
        rangeIndex !== null &&
        subField
      ) {
        const numValue = value === "" ? "" : Number(value);
        const currentRanges = updated[index][field] || [];
        if (subField === "from" && numValue !== "" && rangeIndex > 0) {
          const previousRange = currentRanges[rangeIndex - 1];
          const previousTo = Number(previousRange?.to) || 0;
          if (previousTo > 0 && numValue <= previousTo) {
            alert(
              `❌ Cannot enter ${numValue}.\n\n'From' value must be greater than the previous range's 'To' value (${previousTo}).\n\nSuggested value: ${previousTo + 1}`,
            );
            return prev;
          }
        }
        updated[index] = {
          ...updated[index],
          [field]: updated[index][field].map((range, i) =>
            i === rangeIndex ? { ...range, [subField]: numValue } : range,
          ),
        };
      } else if (subField) {
        if (
          field === "distinctiveNumberFromTo" ||
          field === "transferDistinctiveNoFromTo"
        ) {
          updated[index] = {
            ...updated[index],
            [field]: { ...updated[index][field], [subField]: value },
          };
        } else if (field === "amount") {
          updated[index] = {
            ...updated[index],
            amount: { ...updated[index].amount, [subField]: value },
          };
        }
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, mgt1ShareHolding: updated };
    });
  };

  const addShareHolding = () => {
    setFormData((prev) => ({
      ...prev,
      mgt1ShareHolding: [...prev.mgt1ShareHolding, createShareHolding()],
    }));
  };

  const removeShareHolding = (index) => {
    if (formData.mgt1ShareHolding.length > 1) {
      setFormData((prev) => ({
        ...prev,
        mgt1ShareHolding: prev.mgt1ShareHolding.filter((_, i) => i !== index),
      }));
    }
  };

  const addRange = (holdingIndex, field) => {
    setFormData((prev) => {
      const updated = [...prev.mgt1ShareHolding];
      if (!Array.isArray(updated[holdingIndex][field]))
        updated[holdingIndex][field] = [];
      const currentRanges = updated[holdingIndex][field];
      let suggestedFrom = "";
      if (currentRanges.length > 0) {
        const lastRange = currentRanges[currentRanges.length - 1];
        if (lastRange.to) suggestedFrom = Number(lastRange.to) + 1;
      }
      updated[holdingIndex][field] = [
        ...updated[holdingIndex][field],
        { from: suggestedFrom, to: "" },
      ];
      return { ...prev, mgt1ShareHolding: updated };
    });
  };

  const removeRange = (holdingIndex, field, rangeIndex) => {
    setFormData((prev) => {
      const updated = [...prev.mgt1ShareHolding];
      updated[holdingIndex][field] = updated[holdingIndex][field]
        .filter(Boolean)
        .filter((_, i) => i !== rangeIndex);
      return { ...prev, mgt1ShareHolding: updated };
    });
  };

  const cleanRanges = (ranges = []) =>
    Array.isArray(ranges)
      ? ranges
          .filter((r) => r?.from && r?.to)
          .map((r) => ({ from: Number(r.from), to: Number(r.to) }))
      : [];

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!formData.accountId) {
      const errorMsg = "Please select a company.";
      setError(errorMsg);
      alert(errorMsg);
      return;
    }
    if (!formData.shareClass) {
      const errorMsg = "Please enter share class.";
      setError(errorMsg);
      alert(errorMsg);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const apiData = {
        shareClass: formData.shareClass,
        subClass: formData.subClass,
        nominalValuePerShare: Number(formData.nominalValuePerShare) || 0,
        totalSharesHeld: Number(formData.totalSharesHeld) || 0,
        personalDetails: {
          memberName: formData.personalDetails.memberName,
          jointHoldersName: formData.personalDetails.jointHoldersName || null,
          addressOrRegisteredAddress:
            formData.personalDetails.addressOrRegisteredAddress,
          emailId: formData.personalDetails.emailId,
          cinOrRegistrationNo:
            formData.personalDetails.cinOrRegistrationNo || null,
          uniqueIdentificationNo:
            formData.personalDetails.uniqueIdentificationNo || null,
          parentOrSpouseName:
            formData.personalDetails.parentOrSpouseName || null,
          status: formData.personalDetails.status || null,
          occupation: formData.personalDetails.occupation || null,
          panNo: formData.personalDetails.panNo,
          nationality: formData.personalDetails.nationality || "Indian",
        },
        memberMinorDetails: {
          guardianName: formData.memberMinorDetails.guardianName || null,
          dateOfBirthOfMinor:
            formData.memberMinorDetails.dateOfBirthOfMinor || null,
          dateOfBecomingMember:
            formData.memberMinorDetails.dateOfBecomingMember || null,
          memberShipDetails: {
            declarationDateUnderSection89:
              formData.memberMinorDetails.memberShipDetails
                .declarationDateUnderSection89 || null,
            beneficialOwnerNameAndAddress:
              formData.memberMinorDetails.memberShipDetails
                .beneficialOwnerNameAndAddress || null,
            nominationReceiptDate:
              formData.memberMinorDetails.memberShipDetails
                .nominationReceiptDate || null,
            nomineeNameAndAddress:
              formData.memberMinorDetails.memberShipDetails
                .nomineeNameAndAddress || null,
            sharesKeptInAbeyance:
              Number(
                formData.memberMinorDetails.memberShipDetails
                  .sharesKeptInAbeyance,
              ) || 0,
            lienOnShares:
              formData.memberMinorDetails.memberShipDetails.lienOnShares ||
              null,
            dateOfCessationOfMembership:
              formData.memberMinorDetails.memberShipDetails
                .dateOfCessationOfMembership || null,
          },
        },
        mgt1ShareHolding: formData.mgt1ShareHolding.map((item, index) => ({
          allotmentOrTransferNo:
            item.allotmentOrTransferNo || `ALT-${index + 1}`,
          dateOfAllotmentOrTransfer: item.dateOfAllotmentOrTransfer || null,
          numberOfSharesAllottedOrTransferred:
            Number(item.numberOfSharesAllottedOrTransferred) || 0,
          distinctiveNumberRanges: cleanRanges(item.distinctiveNumberRanges),
          folioOfTransferor: item.folioOfTransferor || null,
          nameOfTransferor: item.nameOfTransferor || null,
          dateOfIssueOrEndorsement: item.dateOfIssueOrEndorsement || null,
          certificateNo: item.certificateNo || null,
          lockInPeriod: item.lockInPeriod || null,
          amount: {
            payable_Amount: Number(item.amount.payable_Amount) || 0,
            paidOrDeemed_Amount: Number(item.amount.paidOrDeemed_Amount) || 0,
            due_Amount: Number(item.amount.due_Amount) || 0,
          },
          considerationOtherThanCash: item.considerationOtherThanCash || null,
          dateOfTransferOrTransmission:
            item.dateOfTransferOrTransmission || null,
          numberOfSharesTransferredOrTransmitted:
            Number(item.numberOfSharesTransferredOrTransmitted) || 0,
          transferDistinctiveNumberRanges: cleanRanges(
            item.transferDistinctiveNumberRanges,
          ),
          folioOfTransferee: item.folioOfTransferee || null,
          nameOfTransferee: item.nameOfTransferee || null,
          balanceSharesAfterTransaction:
            Number(item.balanceSharesAfterTransaction) || 0,
          remarks: item.remarks || null,
          authenticationOrSignature: item.authenticationOrSignature || null,
        })),
      };
      console.log(
        "📤 Submitting MGT-1 data:",
        JSON.stringify(apiData, null, 2),
      );
      const response = await mgt1Api.submitMgt1Form(
        apiData,
        formData.accountId,
        token,
      );
      if (
        response &&
        (response.id || response._id || response.success || response.data)
      ) {
        alert("MGT-1 form submitted successfully!");
        setFormData({
          accountId: "",
          companyName: "",
          shareClass: "",
          subClass: "",
          nominalValuePerShare: "",
          totalSharesHeld: "",
          personalDetails: createPersonalDetails(),
          memberMinorDetails: createMemberMinorDetails(),
          mgt1ShareHolding: [createShareHolding()],
        });
      } else {
        const errorMsg = "Failed to submit form. Please try again.";
        setError(errorMsg);
        alert(errorMsg);
      }
    } catch (err) {
      console.error("❌ Error:", err);
      const errorMsg = `Submission failed: ${err.response?.data?.message || err.message || "Please try again"}`;
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="bg-white border-b-4 px-6 py-2 mb-2 text-center shadow-sm rounded-xl relative">
        <button
          onClick={() => window.history.back()}
          className="absolute top-3 left-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-2xl font-black uppercase tracking-tight">
          Form No. MGT - 1
        </h1>
        <h2 className="text-xl font-bold mt-1">Register of Members</h2>
        <p className="text-[10px] italic text-slate-500 mt-2 max-w-3xl mx-auto">
          [Pursuant to section 88 (1)(a) of the Companies Act, 2013 and rule
          3(1) of the Companies (Management and Administration) Rules, 2014]
        </p>
      </div>

      {/* ── Company Selection ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name of the company *
        </label>
        <select
          value={formData.accountId}
          onChange={handleCompanyChange}
          disabled={loading}
          className="w-50 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select a company</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.accountName || `Company ${company.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div className="mb-3 px-4 py-3 rounded-lg text-sm font-medium border bg-red-50 text-red-800 border-red-200">
          {error}
        </div>
      )}

      {/* ── Main Card ── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg mb-2">
        <div className="bg-primary text-white px-3 py-2">
          <span className="font-semibold text-sm uppercase tracking-widest text-white">
            MGT-1 Register Details
          </span>
        </div>

        <div className="p-3 space-y-2">
          {/* Basic Share Info */}
          <p className="text-xs text-slate-500 italic">
            (TO BE MAINTAINED SEPARATELY FOR EACH CLASS OF SHARES)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            <FormField
              label="Class of shares"
              type="dropdown"
              value={formData.shareClass}
              onChange={(value) => handleRootChange("shareClass", value)}
              options={["EQUITY SHARES", "PREFERENCE SHARES"]}
              required
            />
            <FormField
              label="Sub class of shares"
              value={formData.subClass}
              onChange={(value) => handleRootChange("subClass", value)}
            />
            <FormField
              label="Nominal value per share (₹)"
              value={formData.nominalValuePerShare}
              onChange={(value) =>
                handleRootChange("nominalValuePerShare", value)
              }
              type="number"
            />
            <FormField
              label="Total shares held"
              value={formData.totalSharesHeld}
              onChange={(value) => handleRootChange("totalSharesHeld", value)}
              type="number"
            />
          </div>

          <div className="border-t-2 border-slate-100 pt-3">
            <h3 className="font-bold text-xs uppercase tracking-tight text-slate-700 mb-2">
              Personal Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <FormField
                label="Name of member/holder"
                value={formData.personalDetails.memberName}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "memberName", v)
                }
                required
              />
              <FormField
                label="Joint holder's name (if any)"
                value={formData.personalDetails.jointHoldersName}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "jointHoldersName", v)
                }
              />
              <FormField
                label="Address or registered address"
                value={formData.personalDetails.addressOrRegisteredAddress}
                onChange={(v) =>
                  handlePersonalChange(
                    "personalDetails",
                    "addressOrRegisteredAddress",
                    v,
                  )
                }
                type="description"
                rows={1}
              />
              <FormField
                label="Email ID"
                value={formData.personalDetails.emailId}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "emailId", v)
                }
                type="email"
              />
              <FormField
                label="CIN or registration number"
                value={formData.personalDetails.cinOrRegistrationNo}
                onChange={(v) =>
                  handlePersonalChange(
                    "personalDetails",
                    "cinOrRegistrationNo",
                    v,
                  )
                }
              />
              <FormField
                label="Unique identification number"
                value={formData.personalDetails.uniqueIdentificationNo}
                onChange={(v) =>
                  handlePersonalChange(
                    "personalDetails",
                    "uniqueIdentificationNo",
                    v,
                  )
                }
              />
              <FormField
                label="Father's / Spouse's name"
                value={formData.personalDetails.parentOrSpouseName}
                onChange={(v) =>
                  handlePersonalChange(
                    "personalDetails",
                    "parentOrSpouseName",
                    v,
                  )
                }
              />
              <FormField
                label="Status"
                type="dropdown"
                value={formData.personalDetails.status}
                options={["INDIVIUAL", "BODY CORPORATE", "COMPANY"]}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "status", v)
                }
              />
              <FormField
                label="Occupation"
                value={formData.personalDetails.occupation}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "occupation", v)
                }
              />
              <FormField
                label="PAN number"
                value={formData.personalDetails.panNo}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "panNo", v)
                }
                required
              />
              <FormField
                label="Nationality"
                value={formData.personalDetails.nationality}
                onChange={(v) =>
                  handlePersonalChange("personalDetails", "nationality", v)
                }
              />
            </div>
          </div>

          {/* Minor Details */}
          <div className="border-t-2 border-slate-100 pt-3">
            <h3 className="font-bold text-xs uppercase tracking-tight text-slate-700 mb-2">
              In Case Member is a Minor
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <FormField
                label="Guardian name"
                value={formData.memberMinorDetails.guardianName}
                onChange={(v) =>
                  handlePersonalChange("memberMinorDetails", "guardianName", v)
                }
              />
              <FormField
                label="Date of birth of minor"
                value={formData.memberMinorDetails.dateOfBirthOfMinor}
                onChange={(v) =>
                  handlePersonalChange(
                    "memberMinorDetails",
                    "dateOfBirthOfMinor",
                    v,
                  )
                }
                type="date"
              />
              <FormField
                label="Date of becoming member"
                value={formData.memberMinorDetails.dateOfBecomingMember}
                onChange={(v) =>
                  handlePersonalChange(
                    "memberMinorDetails",
                    "dateOfBecomingMember",
                    v,
                  )
                }
                type="date"
              />
            </div>
          </div>

          {/* Membership Details */}
          <div className="border-t-2 border-slate-100 pt-3">
            <h3 className="font-bold text-xs uppercase tracking-tight text-slate-700 mb-2">
              Details of Membership
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <FormField
                label="Declaration date under section 89"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .declarationDateUnderSection89
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "declarationDateUnderSection89",
                    v,
                  )
                }
                type="date"
              />
              <FormField
                label="Beneficial owner name and address"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .beneficialOwnerNameAndAddress
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "beneficialOwnerNameAndAddress",
                    v,
                  )
                }
              />
              <FormField
                label="Nomination receipt date"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .nominationReceiptDate
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "nominationReceiptDate",
                    v,
                  )
                }
                type="date"
              />
              <FormField
                label="Nominee name and address"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .nomineeNameAndAddress
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "nomineeNameAndAddress",
                    v,
                  )
                }
              />
              <FormField
                label="Shares kept in abeyance"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .sharesKeptInAbeyance
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "sharesKeptInAbeyance",
                    v,
                  )
                }
                type="number"
              />
              <FormField
                label="Lien on shares"
                value={
                  formData.memberMinorDetails.memberShipDetails.lienOnShares
                }
                onChange={(v) =>
                  handlePersonalChange("memberShipDetails", "lienOnShares", v)
                }
              />
              <FormField
                label="Date of cessation of membership"
                value={
                  formData.memberMinorDetails.memberShipDetails
                    .dateOfCessationOfMembership
                }
                onChange={(v) =>
                  handlePersonalChange(
                    "memberShipDetails",
                    "dateOfCessationOfMembership",
                    v,
                  )
                }
                type="date"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Share Holding Sections ── */}
      <div className="space-y-2">
        {formData.mgt1ShareHolding.map((holding, index) => (
          <div
            key={index}
            className="bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
          >
            <div className="bg-primary text-white px-3 py-2 flex justify-between items-center">
              <span className="font-semibold text-sm uppercase tracking-widest text-white">
                Share Holding Details #{index + 1}
              </span>
              {formData.mgt1ShareHolding.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeShareHolding(index)}
                  className="text-[10px] text-red-600 flex flex-row items-center gap-2 font-bold uppercase transition bg-white p-1 rounded-md"
                >
                  <Trash2 size={16}/> <span>Share Holding</span>
                </button>
              )}
            </div>

            <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <FormField
                label="Allotment or transfer number"
                value={holding.allotmentOrTransferNo}
                onChange={(v) =>
                  handleShareHoldingChange(index, "allotmentOrTransferNo", v)
                }
              />
              <FormField
                label="Date of allotment or transfer"
                value={holding.dateOfAllotmentOrTransfer}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "dateOfAllotmentOrTransfer",
                    v,
                  )
                }
                type="date"
                max={new Date().toISOString().split("T")[0]}
              />
              <FormField
                label="Number of shares allotted or transferred"
                value={holding.numberOfSharesAllottedOrTransferred}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "numberOfSharesAllottedOrTransferred",
                    v,
                  )
                }
                type="number"
              />

              {/* Distinctive Number Ranges */}
             <div className="col-span-full border rounded p-2 bg-gray-50">
                 <div className="flex items-end justify-between mb-2">
                  <h4 className="text-sm uppercase font-bold mb-1 underline text-slate-700">
                    Distinctive Number Ranges
                  </h4>
                  <button
                    onClick={() => addRange(index, "distinctiveNumberRanges")}
                    type="button"
                    className="bg-primary text-white text-sm gap-1 px-3 py-1.5 rounded flex flex-row items-center"
                  >
                    <Plus size={18}/> <span>Add Distinctive Range</span>
                  </button>
                </div>

                {holding.distinctiveNumberRanges.map((range, rangeIndex) => (
                  <div
                    key={rangeIndex}
                    className="flex flex-wrap items-end gap-2 mb-1 p-2 bg-white border rounded"
                  >
                    {/* From */}
                    <div className="flex-1 min-w-[100px]">
                      <FormField
                        label="From"
                        type="number"
                        value={range.from || ""}
                        onChange={(v) =>
                          handleShareHoldingChange(
                            index,
                            "distinctiveNumberRanges",
                            v,
                            "from",
                            rangeIndex,
                          )
                        }
                      />
                    </div>

                    {/* To */}
                    <div className="flex-1 min-w-[100px]">
                      <FormField
                        label="To"
                        type="number"
                        value={range.to || ""}
                        onChange={(v) =>
                          handleShareHoldingChange(
                            index,
                            "distinctiveNumberRanges",
                            v,
                            "to",
                            rangeIndex,
                          )
                        }
                      />
                    </div>

                    {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() =>
                          removeRange(
                            index,
                            "distinctiveNumberRanges",
                            rangeIndex,
                          )
                        }
                        className="text-red-500"
                      >
                        <Trash2 size={18}/>
                      </button>
                  </div>
                ))}
              </div>

              <FormField
                label="Folio of transferor"
                value={holding.folioOfTransferor}
                onChange={(v) =>
                  handleShareHoldingChange(index, "folioOfTransferor", v)
                }
              />
              <FormField
                label="Name of transferor"
                value={holding.nameOfTransferor}
                onChange={(v) =>
                  handleShareHoldingChange(index, "nameOfTransferor", v)
                }
              />
              <FormField
                label="Date of issue or endorsement"
                value={holding.dateOfIssueOrEndorsement}
                onChange={(v) =>
                  handleShareHoldingChange(index, "dateOfIssueOrEndorsement", v)
                }
                type="date"
                max={new Date().toISOString().split("T")[0]}
              />
              <FormField
                label="Certificate number"
                value={holding.certificateNo}
                onChange={(v) =>
                  handleShareHoldingChange(index, "certificateNo", v)
                }
              />
              <FormField
                label="Lock-in period"
                value={holding.lockInPeriod}
                onChange={(v) =>
                  handleShareHoldingChange(index, "lockInPeriod", v)
                }
              />
              <FormField
                label="Payable amount (₹)"
                value={holding.amount.payable_Amount}
                onChange={(v) =>
                  handleShareHoldingChange(index, "amount", v, "payable_Amount")
                }
                type="number"
              />
              <FormField
                label="Paid or deemed amount (₹)"
                value={holding.amount.paidOrDeemed_Amount}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "amount",
                    v,
                    "paidOrDeemed_Amount",
                  )
                }
                type="number"
              />
              <FormField
                label="Due amount (₹)"
                value={holding.amount.due_Amount}
                onChange={(v) =>
                  handleShareHoldingChange(index, "amount", v, "due_Amount")
                }
                type="number"
              />
              <FormField
                label="Consideration other than cash"
                value={holding.considerationOtherThanCash}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "considerationOtherThanCash",
                    v,
                  )
                }
              />
              <FormField
                label="Date of transfer or transmission"
                value={holding.dateOfTransferOrTransmission}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "dateOfTransferOrTransmission",
                    v,
                  )
                }
                type="date"
              />
              <FormField
                label="Number of shares transferred or transmitted"
                value={holding.numberOfSharesTransferredOrTransmitted}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "numberOfSharesTransferredOrTransmitted",
                    v,
                  )
                }
                type="number"
              />

              {/* Transfer Distinctive Number Ranges */}
              <div className="col-span-full border rounded p-2 bg-gray-50">
                <div className="flex items-end justify-between mb-2">
                  <h4 className="text-sm font-bold uppercase mb-1 text-slate-700 underline">
                    Transfer Distinctive Number Ranges
                  </h4>
                  <button
                    onClick={() => addRange(index, "transferDistinctiveNumberRanges")}
                    type="button"
                    className="bg-primary text-white text-sm gap-1 px-3 py-1.5 rounded flex flex-row items-center"
                  >
                    <Plus size={18}/> <span>Add Transfer Range</span>
                  </button>
                </div>

                {holding.transferDistinctiveNumberRanges.map(
                  (range, rangeIndex) => (
                    <div key={rangeIndex} className="flex flex-wrap items-end gap-2 mb-1 p-2 bg-white border rounded">
                      <div className="flex-1">
                        <FormField
                          label="From"
                          type="number"
                          value={range.from || ""}
                          onChange={(v) =>
                            handleShareHoldingChange(
                              index,
                              "transferDistinctiveNumberRanges",
                              v,
                              "from",
                              rangeIndex,
                            )
                          }
                        />
                      </div>

                      <div className="flex-1">
                        <FormField
                          label="To"
                          type="number"
                          value={range.to || ""}
                          onChange={(v) =>
                            handleShareHoldingChange(
                              index,
                              "transferDistinctiveNumberRanges",
                              v,
                              "to",
                              rangeIndex,
                            )
                          }
                        />
                      </div>

                        <button
                          type="button"
                          onClick={() =>
                            removeRange(
                              index,
                              "transferDistinctiveNumberRanges",
                              rangeIndex,
                            )
                          }
                          className="text-red-500"
                        >
                          <Trash2 size={18}/>
                        </button>
                    </div>
                  ),
                )}
              </div>

              <FormField
                label="Folio of transferee"
                value={holding.folioOfTransferee}
                onChange={(v) =>
                  handleShareHoldingChange(index, "folioOfTransferee", v)
                }
              />
              <FormField
                label="Name of transferee"
                value={holding.nameOfTransferee}
                onChange={(v) =>
                  handleShareHoldingChange(index, "nameOfTransferee", v)
                }
              />
              <FormField
                label="Balance shares after transaction"
                value={holding.balanceSharesAfterTransaction}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "balanceSharesAfterTransaction",
                    v,
                  )
                }
                type="number"
                required
              />
              <FormField
                label="Authentication or signature"
                value={holding.authenticationOrSignature}
                onChange={(v) =>
                  handleShareHoldingChange(
                    index,
                    "authenticationOrSignature",
                    v,
                  )
                }
              />
              <div className="col-span-full">
                <FormField
                  label="Remarks"
                  value={holding.remarks}
                  onChange={(v) =>
                    handleShareHoldingChange(index, "remarks", v)
                  }
                  type="description"
                  rows={2}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Action Buttons ── */}
      <div className="flex flex-row justify-between items-center mt-2 mb-4">
        <button
          type="button"
          onClick={addShareHolding}
          className="bg-primary text-white px-3 py-1.5 rounded flex flex-row items-center"
        >
          <Plus/> <span>Add Share Holder</span>
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={submitting || !formData.accountId}
          className="bg-primary text-white px-3 py-1.5 rounded flex flex-row items-center"
        >
          {submitting ? "Submitting..." : "Submit MGT-1 Form"}
        </button>
      </div>
    </div>
  );
};

export default MgtForm;
